import {useState} from 'react';

export interface UseQueueSet<T extends string> {
  keys: T[];
  open: (key: T) => void;
  close: () => void;
  clear: () => void;
}

export const useQueueSet = <T extends string>(initialValue: T[] = []): UseQueueSet<T> => {
  // Internal hooks
  const [keys, setKeys] = useState<T[]>(initialValue);

  // Functions
  const open = (key: T) => {
    setKeys(keys => {
      if (keys.includes(key)) {
        return keys;
      }
      return [...keys, key];
    });
  };
  const close = () => {
    setKeys(keys => keys.slice(0, -1));
  };
  const clear = () => {
    setKeys([]);
  };

  return {
    keys,
    open,
    close,
    clear,
  };
};

export interface UseObjectQueueSetParams<T extends any> {
  initial?: T[];
  allowEmpty?: boolean;
  findCallbackComparable?: (a: T, b: T) => boolean;
}
export interface UseObjectQueueSet<T extends any> {
  values: T[];
  open: (key: T) => void;
  close: (key?: T) => void;
  clear: () => void;
  reset: () => void;
}

export const useObjectQueueSet = <T extends any>(params: UseObjectQueueSetParams<T> = {}): UseObjectQueueSet<T> => {
  // Params destructure and defaults
  const {initial = [], findCallbackComparable = (a, b) => a === b} = params;

  // Internal hooks
  const [values, setValues] = useState<T[]>(initial);

  // Functions
  const open = (newValue: T) => {
    if (!!values.find(findCallbackComparableValue => findCallbackComparable(findCallbackComparableValue, newValue))) {
      return;
    }
    setValues(currentState => [...currentState, newValue]);
  };
  const close = (specificValue?: T) => {
    if (specificValue !== undefined) {
      setValues(currentState =>
        currentState.filter(
          findCallbackComparableValue => !findCallbackComparable(findCallbackComparableValue, specificValue),
        ),
      );
      return;
    }
    setValues(currentState => currentState.slice(0, -1));
  };
  const clear = () => {
    setValues([]);
  };
  const reset = () => {
    setValues(initial);
  };

  return {
    values,
    open,
    close,
    clear,
    reset,
  };
};
